import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { IMAGES } from "@/lib/assets";

const values = [
  {
    title: "Reliable by design",
    desc: "Fast, secure, always-on booking websites your customers can trust — hosted and maintained by us.",
  },
  {
    title: "Clean, simple UX",
    desc: "No clutter. Customers book in seconds and you manage the whole day from one clear dashboard.",
  },
  {
    title: "Built to grow",
    desc: "Start simple, then switch on loyalty, memberships, payments and marketing as your business grows.",
  },
];

export default function About() {
  useSeo({
    title: "About Easy Booking Solutions | Simplifying Bookings For Service Businesses",
    description:
      "Easy Booking Solutions helps service businesses get online with beautiful, fully-bookable websites. Learn our story and why we build booking software that just works.",
    path: "/about",
  });

  return (
    <div data-testid="about-page">
      <section className="px-6 md:px-12 pt-20 pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
            About us
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#0A0A0A] mt-4">
            Booking software that just works — for every industry.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mt-6">
            Easy Booking Solutions builds and manages beautiful, fully-bookable websites for
            service businesses. We started with massage and day spas, and we're expanding into
            more industries so any local business can get online in minutes.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src={IMAGES.about}
            alt="Team building booking websites for service businesses"
            className="w-full h-[380px] md:h-[460px] object-cover rounded-3xl shadow-lg"
          />
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A]">
              Our mission
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mt-5">
              Running a service business is hard enough without wrestling with technology. We take
              care of the website, the online booking, the payments and the hosting — so owners can
              focus on delivering a great experience to their customers.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4">
              One platform, one simple monthly plan, and a booking site that's live the same day.
              That's the Easy Booking promise.
            </p>
            <Link
              to="/industries"
              data-testid="about-industries-cta"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-gray-800 transition-all"
            >
              Explore our industries <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 py-20 bg-[#F4FBF6]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#0A0A0A] text-center">
            What we stand for
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white border border-gray-100 rounded-3xl p-8"
                data-testid={`value-card-${i}`}
              >
                <CheckCircle2 className="h-8 w-8 text-[#3EA65B]" />
                <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mt-5">{v.title}</h3>
                <p className="text-gray-600 leading-relaxed mt-3">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
